import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ShieldCheck, Grid3x3, Table2, Truck, CalendarRange, Building2, KeyRound, ListChecks } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Breadcrumbs from "@/components/admin/Breadcrumbs";

const TILES = [
  {
    to: "/admin/users",
    label: "Users",
    icon: Users,
    desc: "Create, edit, disable, reset passwords.",
    countKey: "users",
    perm: "users.read",
  },
  {
    to: "/admin/roles",
    label: "Roles",
    icon: KeyRound,
    desc: "Define roles and their permission sets.",
    countKey: null,
    perm: "roles.read",
  },
  {
    to: "/admin/matrix",
    label: "Permissions Matrix",
    icon: ListChecks,
    desc: "Role × app visibility grid for the tenant.",
    countKey: null,
    perm: "roles.read",
  },
  {
    to: "/admin/apps",
    label: "App Registry",
    icon: Grid3x3,
    desc: "Add, edit, toggle SSO + embeddable for tenant apps.",
    countKey: null,
    perm: "app_registry.read",
  },
  {
    to: "/admin/settings",
    label: "Tenant Settings",
    icon: ShieldCheck,
    desc: "Branding, logo, and tenant-level members.",
    countKey: null,
    perm: "tenant.manage",
  },
  {
    to: "/admin/vehicles",
    label: "Vehicles",
    icon: Truck,
    desc: "Fleet registry for calendar bookings.",
    countKey: "vehicles",
    perm: "calendar.admin",
  },
  {
    to: "/admin/calendar-types",
    label: "Calendar Types",
    icon: CalendarRange,
    desc: "Colour-coded entry types and resources.",
    countKey: "types",
    perm: "calendar.admin",
  },
  {
    to: "/admin/clients",
    label: "Clients",
    icon: Building2,
    desc: "Tag bookings and meetings to a client.",
    countKey: "clients",
    perm: "calendar.admin",
  },
];

export default function AdminHome() {
  const { hasPerm } = useAuth();
  const [counts, setCounts] = useState({ users: 0, vehicles: 0, types: 0, clients: 0 });

  const visibleTiles = TILES.filter((t) => !t.perm || hasPerm(t.perm));

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const results = await Promise.allSettled([
          hasPerm("users.read") ? api.get("/admin/users") : Promise.resolve({ data: [] }),
          hasPerm("calendar.admin") ? api.get("/admin/vehicles") : Promise.resolve({ data: [] }),
          hasPerm("calendar.admin") ? api.get("/admin/calendar-types") : Promise.resolve({ data: [] }),
          hasPerm("calendar.admin") ? api.get("/admin/clients") : Promise.resolve({ data: [] }),
        ]);
        if (!alive) return;
        const get = (i) => (results[i].status === "fulfilled" ? results[i].value.data : []);
        setCounts({
          users: get(0).length,
          vehicles: get(1).length,
          types: get(2).length,
          clients: get(3).length,
        });
      } catch (e) {
        // resilient
      }
    })();
    return () => {
      alive = false;
    };
  }, [hasPerm]);

  return (
    <div data-testid="admin-home" className="mx-auto w-full max-w-6xl">
      <Breadcrumbs />
      <div className="mb-8">
        <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
          Admin
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Manage users, roles, apps, vehicles, clients and calendar types.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visibleTiles.map((t) => {
          const Icon = t.icon;
          const count = t.countKey ? counts[t.countKey] : null;
          return (
            <Link
              to={t.to}
              key={t.to}
              data-testid={`admin-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-cargodash-primary-500/50 hover:shadow"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cargodash-primary-50 text-cargodash-primary-700">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-display text-sm font-semibold text-slate-900">
                    {t.label}
                  </div>
                  {count !== null && (
                    <div className="text-[11px] text-slate-500">
                      {count} {count === 1 ? t.label.toLowerCase().replace(/s$/, "") : t.label.toLowerCase()}
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-500">{t.desc}</p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
